import { READINESS_RETRY_BUTTON } from 'modules/overlay-v2/domain/constants/readiness';
import { OverlayReadinessStep } from 'modules/overlay-v2/domain/interfaces/readiness';
import React, { useEffect, useState } from 'react';
import { theme } from 'styles/theme/theme';
import UDText from 'ud-ui/components/text';

import StatusIcon from './status-icon';
import { HintBox, RetryButton, StepBody, StepRow, STATUS_COLORS } from './styles';

interface ReadinessStepRowProps {
  step: OverlayReadinessStep;
  /** Порядковый номер строки — задаёт задержку каскадного появления. */
  index: number;
  onRetry: () => void;
}

/**
 * Секунды до автоматического повтора. Тикают локально, а не в сторе: обратный отсчёт
 * нужен одной строке, а перерисовывать из-за него весь оверлей раз в секунду незачем.
 */
function useSecondsLeft(retryAt?: number): number | undefined {
  const [secondsLeft, setSecondsLeft] = useState(() => toSecondsLeft(retryAt));

  useEffect(() => {
    if (retryAt === undefined) {
      setSecondsLeft(undefined);
      return;
    }

    setSecondsLeft(toSecondsLeft(retryAt));
    const interval = setInterval(() => setSecondsLeft(toSecondsLeft(retryAt)), 1000);
    return () => clearInterval(interval);
  }, [retryAt]);

  return secondsLeft;
}

function toSecondsLeft(retryAt?: number): number | undefined {
  if (retryAt === undefined) return undefined;
  return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
}

function ReadinessStepRow({ step, index, onRetry }: ReadinessStepRowProps) {
  const secondsLeft = useSecondsLeft(step.retryAt);
  const isTitleMuted = step.status === 'pending';

  return (
    <StepRow stepStatus={step.status} index={index}>
      <StatusIcon status={step.status} />

      <StepBody>
        <UDText variant={'text-2'} color={isTitleMuted ? theme.colors.WhiteGray : theme.colors.SurfaceWhite}>
          {step.title}
        </UDText>

        {step.detail && (
          <UDText variant={'subtitle-2'} relaxed color={STATUS_COLORS[step.status]}>
            {step.detail}
          </UDText>
        )}

        {secondsLeft !== undefined && (
          <UDText variant={'subtitle-2'} color={theme.colors.WhiteGray}>
            {secondsLeft > 0 ? `Повторим через ${secondsLeft} с` : 'Повторяем…'}
          </UDText>
        )}

        {step.hint && (
          <HintBox>
            <UDText variant={'subtitle-2'} relaxed color={theme.colors.WhiteGray}>
              {step.hint}
            </UDText>
          </HintBox>
        )}

        {step.status === 'failed' && step.isRetryable && (
          <RetryButton type={'button'} onClick={onRetry}>
            {READINESS_RETRY_BUTTON}
          </RetryButton>
        )}
      </StepBody>
    </StepRow>
  );
}

export default React.memo(ReadinessStepRow);
